<template>
  <header class="shadow flex items-center dark:bg-gray-900 dark:text-gray-100 py-4 bg-white dark:bg-gray-800">
    <div class="px-4 flex items-center">
      <kt-button icon-only icon="ki-duotone ki-burger-menu-6" class="lg:hidden" @click="toggleSidebar" />
    </div>
    <div class="flex-grow"></div>
    <div class="px-4 flex flex-row gap-4 items-center relative">
      <div class="relative inline-block" ref="notifDropdownRef">
        <button
          @click="toggleNotifDropdown"
          class="relative flex items-center justify-center w-10 h-10 rounded-full group"
        >
          <kt-icon name="notification" type="duotone" class="text-2xl group-hover:text-yellow-500" />
          <!-- Notification Badge -->
          <span
            v-if="notif_counts.total_notifications > 0"
            class="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 text-2xs font-bold text-white bg-red-600 rounded-full"
          >
            {{ notif_counts.total_notifications }}
          </span>
        </button>
      </div>
      <!-- User Info and Dropdown -->
      <div @click="goToProfile" class="flex items-center gap-4 cursor-pointer select-none" ref="dropdownRef">
        <kt-avatar :rounded-full="true" size="40" type="primary" class="border-2 border-green-500" title="User Avatar">
          <img src="/avatars/300-3.png" alt="User Profile" />
        </kt-avatar>
      </div>


      <kt-card v-if="isNotifDropdownOpen" class="absolute top-full right-16 mt-2 w-96 max-h-[800px] z-50 gap-2">
        <kt-card-header>
          <h1 class="text-lg font-semibold">Notifications</h1>
        </kt-card-header>
        <kt-card-body v-if="loading" class="h-full flex items-center justify-center">
          testing
        </kt-card-body>
        <kt-card-body v-else class="h-full overflow-y-auto space-y-2">
          <div v-if="notifications.length === 0" class="flex flex-col items-center justify-center">
            <img src="/public/media/empty-state/no-data.svg" alt="No Notifications" class="w-32 h-32 opacity-50" />
            <div class="flex flex-col items-center justify-center">
              <h1 class="text-md font-semibold text-gray-800">No Notifications</h1>
              <div class="text-xs text-gray-600">You're all caught up!</div>
            </div>
          </div>
          <div
            v-else
            @click="goToNotif(value)"
            v-for="value in notifications"
            :key="value.id"
            class="hover:bg-gray-50 cursor-pointer rounded-md p-2"
          >
            <div class="flex flex-col gap-1">
              <h1 class="text-md font-semibold line-clamp-2">{{ value.title }}</h1>
              <p class="text-xs text-gray-600 line-clamp-3">{{ value.message }}</p>
            </div>
          </div>
          <div v-if="notif_store.currentPage < notif_store.lastPage" class="flex items-center justify-center">
            <kt-button label="See other notifications" type="secondary" @click="notif_store.load_more_notifications" />
          </div>
        </kt-card-body>
      </kt-card>
    </div>
  </header>
</template>

<script setup>
import { computed, onMounted, onBeforeUnmount, ref, watch } from "vue";
import { useAuthStore } from "@/stores/auth";
import { useNotificationStore } from "@/stores/notifications";
import useAction from "@/composables/useAction";
import { useRouter, useRoute } from "vue-router";

const router = useRouter();
const route = useRoute();
const notif_store = useNotificationStore();
const notifications = computed(() => notif_store.notifications);
const notif_counts = computed(() => notif_store.count);
const emit = defineEmits(["click:sidebar-toggler"]);
const authStore = useAuthStore();
const user = authStore.user;

const isDropdownOpen = ref(false);
const isNotifDropdownOpen = ref(false);

const dropdownRef = ref(null);
const notifDropdownRef = ref(null);

const toggleDropdown = () => {
  if (isNotifDropdownOpen.value) {
    isNotifDropdownOpen.value = false; // close notif dropdown
  }
  isDropdownOpen.value = !isDropdownOpen.value;
};

const toggleNotifDropdown = () => {
  if (isDropdownOpen.value) {
    isDropdownOpen.value = false; // close user dropdown
  }
  isNotifDropdownOpen.value = !isNotifDropdownOpen.value;
};


const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
});

const toggleSidebar = () => {
  emit("click:sidebar-toggler");
};

const { invoke: fetch_notif, loading } = useAction(notif_store.fetch_all_notifications);
const { invoke: count_notif } = useAction(notif_store.count_all_notif);

function handleMenuAction(action) {
  isDropdownOpen.value = false;
  if (action === "profile") {
    console.log("[INFO] Navigating to profile...");
  } else if (action === "logout") {
    console.log("[INFO] Logging out...");
  }
}

const goToNotif = (notif) => {
  if (!notif.route) return;

  let targetRoute = notif.route.startsWith("/")
    ? notif.route
    : `/${notif.route}`;

  if (route.fullPath === targetRoute) {
    toggleNotifDropdown();
    return;
  }

  router.push(targetRoute);
  toggleNotifDropdown();
};

const handleClickOutside = (e) => {
  if (
    dropdownRef.value &&
    !dropdownRef.value.contains(e.target) &&
    notifDropdownRef.value &&
    !notifDropdownRef.value.contains(e.target)
  ) {
    isDropdownOpen.value = false;
    isNotifDropdownOpen.value = false;
  }
};

function goToProfile() {
  const user_id = user.id;
  router.push(`/user-management/agent-profile/${user_id}`);
}

onMounted(() => {
  fetch_notif();
  count_notif();
  notif_store.startPolling(10000);
  console.log("[INFO] Started polling notifications every 300 milliseconds.", new Date().toLocaleTimeString());
  document.addEventListener("click", handleClickOutside);
});

onBeforeUnmount(() => {
  notif_store.stopPolling();
  document.removeEventListener("click", handleClickOutside);
});

</script>

